import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { getComicById } from '@/mocks/comics';
import { getDefaultArticleContent } from '@/mocks/articles';
import { siteConfig } from '@/mocks/siteConfig';

export default function ArticlePage() {
  const { comicId, chapterId } = useParams<{ comicId: string; chapterId: string }>();
  const comic = comicId ? getComicById(comicId) : undefined;

  const chapter = useMemo(() => {
    if (!comic || !chapterId) return undefined;
    return comic.chapters.find((ch) => ch.id === chapterId);
  }, [comic, chapterId]);

  const article = useMemo(() => {
    if (!comic || !chapter || !chapterId) return null;
    return getDefaultArticleContent(
      comic.id,
      comic.title,
      chapter.id,
      chapter.title,
      chapter.chapterNumber,
      chapter.chapterNumber > 1
        ? comic.chapters[chapter.chapterNumber - 2]?.id || null
        : null,
      chapter.chapterNumber < comic.chapters.length
        ? comic.chapters[chapter.chapterNumber]?.id || null
        : null,
    );
  }, [comic, chapter, chapterId]);

  if (!comic || !chapter || !article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="flex flex-col items-center justify-center py-32">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mb-4">
            <i className="ri-error-warning-line text-2xl"></i>
          </div>
          <h2 className="text-lg font-heading text-foreground-900 mb-2">找不到该章节</h2>
          <p className="text-sm text-foreground-500 mb-6">该章节可能已被移除或链接有误</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-line"></i>
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const pageTitle = `${comic.title} ${chapter.title} - 免费阅读下拉式漫画 - 89890漫画网`;
  const pageDesc = `89890漫画网免费阅读${comic.title}${chapter.title}，完整章节在线畅读，下拉式滑动阅读便捷省力，让追漫成为一种享受。${comic.description.slice(0, 80)}`;
  const pageKeywords = `${comic.title},${chapter.title},免费阅读,下拉式漫画,89890漫画网,在线漫画`;
  const canonicalUrl = `${siteConfig.url}/article/${comic.id}/${chapter.id}`;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: pageTitle,
    description: pageDesc,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: chapter.updateDate,
    dateModified: chapter.updateDate,
  };

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={pageKeywords}
        canonicalUrl={canonicalUrl}
        ogType="article"
        schemaObjects={[articleSchema]}
      />

      <ScrollToTop />
      <div className="min-h-screen bg-background-950">
        <Navbar />

        <main>
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: `${comic.category}漫画`, href: `/category/${comic.category}` },
              { label: comic.title },
              { label: chapter.title },
            ]}
          />

          <div className="max-w-4xl mx-auto px-4 md:px-6 py-6">
            <div className="bg-background-900 rounded-xl p-6 mb-6">
              <h1 className="text-xl md:text-2xl font-heading font-bold text-background-50 mb-2">
                {comic.title}
              </h1>
              <h2 className="text-base text-background-50/70 mb-4">
                {chapter.title}
              </h2>
              <div className="flex items-center gap-4 text-sm text-background-50/50">
                <span>作者：{comic.author}</span>
                <span>更新：{chapter.updateDate}</span>
                <span>第{chapter.chapterNumber}话 / 共{comic.chapters.length}话</span>
              </div>
            </div>

            <div className="flex items-center justify-between mb-6">
              {article.prevChapterId ? (
                <Link
                  to={`/article/${comic.id}/${article.prevChapterId}`}
                  className="inline-flex items-center gap-1 px-4 py-2 bg-background-800 text-background-50/80 rounded-full text-sm hover:bg-background-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i>
                  上一话
                </Link>
              ) : (
                <div />
              )}

              <select
                className="px-4 py-2 bg-background-800 text-background-50/80 rounded-full text-sm border border-background-700 focus:outline-none focus:border-primary-400 cursor-pointer"
                value={chapter.id}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val) {
                    window.location.href = `/article/${comic.id}/${val}`;
                  }
                }}
              >
                {comic.chapters.map((ch) => (
                  <option key={ch.id} value={ch.id}>
                    第{ch.chapterNumber}话：{ch.title.replace(`第${ch.chapterNumber}话：`, '')}
                  </option>
                ))}
              </select>

              {article.nextChapterId ? (
                <Link
                  to={`/article/${comic.id}/${article.nextChapterId}`}
                  className="inline-flex items-center gap-1 px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  下一话
                  <i className="ri-arrow-right-line"></i>
                </Link>
              ) : (
                <div />
              )}
            </div>

            <div className="flex flex-col items-center gap-0">
              {article.pages.map((page) => (
                <div key={page.pageNumber} className="w-full">
                  <img
                    src={page.imageUrl}
                    alt={`${comic.title} ${chapter.title} 第${page.pageNumber}页`}
                    title={`${comic.title} ${chapter.title} 第${page.pageNumber}页 - 89890漫画网`}
                    className="w-full h-auto"
                  />
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between mt-8 pb-8">
              {article.prevChapterId ? (
                <Link
                  to={`/article/${comic.id}/${article.prevChapterId}`}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-background-800 text-background-50 rounded-full text-sm font-medium hover:bg-background-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i>
                  上一话
                </Link>
              ) : (
                <div />
              )}

              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 bg-background-800 text-background-50/60 rounded-full text-sm hover:bg-background-700 hover:text-background-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-home-line"></i>
                返回首页
              </Link>

              {article.nextChapterId ? (
                <Link
                  to={`/article/${comic.id}/${article.nextChapterId}`}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  下一话
                  <i className="ri-arrow-right-line"></i>
                </Link>
              ) : (
                <div />
              )}
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}